/**
 * Phase 6e2c-0 — SELECTOR TAB REGISTRY (§30.3 lazy per-selector construction).
 *
 * THE BOARD registry. The chassis renders exactly ONE active tab + its content
 * div + .control-buttons. This is the React analogue of the legacy
 * `fn.require('view.component_selector.<name>.selector')` lazy load + the
 * `show_tab` Handlebars switch in component_selector.tmpl.
 *
 * ★ Phase 6.5 — the HEADER (`show_tab:'all'`) 7-tab variant is NO LONGER
 * deferred: it lives in its own registry,
 * `pages/dashboard/components/header/IndependentRatingPopup.tsx`
 * (`HEADER_SELECTORS`). It is intentionally separate because the header host
 * offers a DIFFERENT, SMALLER set — 7 tabs wired (fan, coil, gas_heater[hidden],
 * energy_wheel, plate_heat_exchanger, uvc[hidden], filter[hidden]) and 6 board
 * selectors ABSENT entirely (special_options, evap_pack, opening, hood, damper,
 * louver — the `'all'` branch of component_selector.tmpl never emits them and
 * `AccessoryView` is only preloaded in the board branch, component_selector.js
 * :49-52) — and because the header entries must pass the header seams
 * (headerMode / eventScope:'header' / bus:headerRatingBus / modelId:0 / ahu:null).
 *
 * ADMITTING THE OTHER SELECTORS (later 6e2c waves):
 *   - evap_pack / accessory(opening|hood|damper|louver) / filter port to React →
 *     register a `render` here, same as special_options. `isAccessory:true`
 *     mirrors the template's `is_accessory_selector` (hides #rate, #select starts
 *     enabled) for opening/hood/damper/louver.
 *   - the 6 PARKED vendor selectors (fan/coil/energy_wheel/plate_heat_exchanger/
 *     gas_heater/uvc) keep `render: undefined` → the chassis draws the labelled
 *     ParkedVendorPlaceholder so the tab strip + dialog are structurally complete
 *     without loading any legacy view (the vendor /rating/ dispatch stays
 *     legacy/parked per §32; that is explicitly NOT built in this phase).
 */

import type { JSX } from 'react';
import type { SelectorTabDef, SelectorTabProps } from './selectorTypes';
import { SpecialOptionsSelector } from './SpecialOptionsSelector';
import { EvapPackSelector } from './EvapPackSelector';
import { AccessorySelector } from './AccessorySelector';
import { FilterSelector } from './FilterSelector';
import { UvcSelector } from './UvcSelector';
import { GasHeaterSelector } from './GasHeaterSelector';
import { EnergyWheelSelector } from './EnergyWheelSelector';
import { PlateHeatExchangerSelector } from './PlateHeatExchangerSelector';
import { CoilSelector } from './CoilSelector';
import { FanSelector } from './FanSelector';

export interface SelectorRegistryEntry {
  def: SelectorTabDef;
  /** undefined → parked vendor tab (placeholder body, no functionality). */
  render?: (props: SelectorTabProps) => JSX.Element;
}

/**
 * The label strip matches component_selector.tmpl's per-`show_tab` <a> text
 * verbatim (Fan / Coil / HEAT WHEEL / PLATE HEAT EXCHANGER / UV-C / Filter /
 * Special Options / EVAPORATIVE COOLER / Opening / Hood / Damper / Louver).
 */
export const SELECTOR_REGISTRY: Record<string, SelectorRegistryEntry> = {
  // ---- WAVE 1 real React port ----
  special_options: {
    def: { name: 'special_options', label: 'Special Options' },
    render: (props) => <SpecialOptionsSelector {...props} />,
  },

  // ---- WAVE 2 real React port (6e2c-2) ----
  evap_pack: {
    // component_selector.tmpl:50 reads `EVAPORATIVE COOLER` — the one label in
    // this table that had drifted from the legacy strip.
    def: { name: 'evap_pack', label: 'EVAPORATIVE COOLER', rateNonClickable: true },
    render: (props) => <EvapPackSelector {...props} />,
  },

  // ---- WAVE 3 real React port (6e2c-3) — the accessory selector ----
  // ONE shared <AccessorySelector> serves all four (legacy: one View, 4 tmpls).
  opening: {
    def: { name: 'opening', label: 'Opening', isAccessory: true },
    render: (props) => <AccessorySelector type="opening" {...props} />,
  },
  hood: {
    def: { name: 'hood', label: 'Hood', isAccessory: true },
    render: (props) => <AccessorySelector type="hood" {...props} />,
  },
  damper: {
    def: { name: 'damper', label: 'Damper', isAccessory: true },
    render: (props) => <AccessorySelector type="damper" {...props} />,
  },
  louver: {
    def: { name: 'louver', label: 'Louver', isAccessory: true },
    render: (props) => <AccessorySelector type="louver" {...props} />,
  },

  // ---- WAVE 4 real React port (6e2c-4) — the filter selector (shim #8 cut) ----
  // Filter selector + filter FORM migrate in the SAME cut (§50.2 row 8): the
  // selector re-homes the form's catalogue smart-list seam into the dialog and
  // restores it on close. Operates on the LIVE form model via window.
  // __FORM_SHIM__[id] (no staging clone).
  filter: {
    def: { name: 'filter', label: 'Filter' },
    render: (props) => <FilterSelector {...props} />,
  },

  // ---- WAVE 1 vendor-port real React port — UV-C selector (§33.6) ----
  // Server-side rate (PATCH ahu/uvcs/<id> {rate:true}); NO /rating/ dispatch.
  // Opened from the ACCESSORIES tab (the uvc accessory form's Select heading →
  // rating_mode:activate {name:'uvc', current_model:{id:accessoryId}, section})
  // — the React analogue of legacy tab.accessories openRatingMode. Has a real
  // #rate (gated on required fields) + #print; #select starts disabled (enabled
  // only after a rate produces outputs) — so NOT isAccessory.
  uvc: {
    def: { name: 'uvc', label: 'UV-C' },
    render: (props) => <UvcSelector {...props} />,
  },

  // ---- WAVE 2 vendor-port real React port — GAS HEATER selector (§33.5) ----
  // Server-side CMS catalog search (NOT an external vendor /rating/ dispatch):
  // RATE/SELECT = a model.save PATCH (rate:true/false + isSelect); PRINT = the
  // ONLY celery PDF flow (POST rating/pdf_rating/ → {task_id} → socket → window
  // .open). Opened from the gas_heater FORM's rating label (withRating:true);
  // §30.4 key gas_heater_<id>:selector:<action>. Has #rate (gated on required
  // fields) + #print; #select enabled when the model carries a rating.
  gas_heater: {
    def: { name: 'gas_heater', label: 'GAS HEATER' },
    render: (props) => <GasHeaterSelector {...props} />,
  },

  // ---- WAVE 3 vendor-port real React port — ENERGY WHEEL selector (§33.3) ----
  // The FIRST external-vendor selector: Rate fires POST /api/v1.0/rating/
  // {part:'erwheel'} → Annexair/Innergy/Heatex/AI-Rotor (the SHARED
  // useRatingDispatch). SELECT writes via current_model.set → auto-save +
  // energy_wheel_params blob. Opened from the EW FORM's rating label
  // (withRating:true). Q22 naming split: the form's partName is `er_wheel`
  // (COMPONENT_PART_NAMES.energy_wheel) so the §30.4 fan-out key the chassis
  // emits is `er_wheel_<id>:selector:<action>` — the selector subscribes to the
  // name it was opened under (activate.name). Register all THREE aliases
  // (er_wheel = the opener's partName; energy_wheel = the legacy event prefix +
  // registry name; rotary = the navy hasRating alias / Q22) → one render fn.
  //
  // ★ The two ALIASES carry `showTab: 'energy_wheel'`. dev only ever activates
  // this dialog as `energy_wheel` (energy_wheel/view/form.js:44), so that is the
  // `show_tab` string its chassis stamps into `#rating_mode_<show_tab>_content`
  // and the `.ahu-rate-content`/`.content-pager` classes — and the string all 74
  // `#rating_mode_energy_wheel_content …` rules select on. Opened as `er_wheel`
  // (which is what the board form's partName is) React emitted
  // `#rating_mode_er_wheel_content`, so the whole EW stylesheet was dead: the
  // Performance pane lost its unit-span float, its 75% input width, its
  // right-alignment and its Velocity-Range 30% split, and `showReset` below —
  // a `show_tab` gate — never matched either, so #reset was missing too.
  er_wheel: {
    def: { name: 'er_wheel', showTab: 'energy_wheel', label: 'HEAT WHEEL' },
    render: (props) => <EnergyWheelSelector {...props} />,
  },
  energy_wheel: {
    def: { name: 'energy_wheel', label: 'HEAT WHEEL' },
    render: (props) => <EnergyWheelSelector {...props} />,
  },
  rotary: {
    def: { name: 'rotary', showTab: 'energy_wheel', label: 'HEAT WHEEL' },
    render: (props) => <EnergyWheelSelector {...props} />,
  },

  // ---- WAVE 4 vendor-port real React port — PLATE HEAT EXCHANGER selector (§33.4) ----
  // Closely mirrors the W3 energy_wheel selector (same dependency-graph +
  // summer/winter shape). Rate fires POST /api/v1.0/rating/
  // {part:'plate_heat_exchanger'} → Recuperator (167) / Heatex (52) via the
  // SHARED useRatingDispatch. SELECT writes: current_model.set → auto-save +
  // self.model.set(rating_fields) → rated_phx_model PATCH (rating_mode/<id>) +
  // master_phx_id.resetFilter. Opened from the PHX FORM's rating label
  // (withRating:true); §30.4 key plate_heat_exchanger_<id>:selector:<action>.
  plate_heat_exchanger: {
    def: { name: 'plate_heat_exchanger', label: 'PLATE HEAT EXCHANGER' },
    render: (props) => <PlateHeatExchangerSelector {...props} />,
  },

  // ---- WAVE 5 vendor-port real React port — COIL selector (§33.2) ----
  // The COIL selector has TWO rate paths: external_rating===false → POST
  // /ahu/rating_database_coil_selection/ (KSDM master-coil DB, manufacturer
  // 'manual'); else → POST /api/v1.0/rating/ {part:'coil'} with the derived
  // manufacturer (mfg.name.toLowerCase(); mfg_id 21 hard-errors) via the SHARED
  // useRatingDispatch. SELECT = the FOUR-WAY write: section.set + project.save +
  // current_model.set/save + rated_coil_model PATCH (rating_mode_coils/<id>).
  // Opened from the COIL FORM's rating label (withRating:true); §30.4 key
  // coil_<id>:selector:<action>. ALSO listens update_rating_dialog:<id>.
  coil: {
    def: { name: 'coil', label: 'Coil' },
    render: (props) => <CoilSelector {...props} />,
  },

  // ---- WAVE 6 vendor-port real React port — FAN selector (§33.1) — the LARGEST
  // + FINAL vendor selector (6,249 lines). Rate fires the EXTERNAL vendor POST
  // /api/v1.0/rating/ {part:'fan'} (×up-to-3 sites) → greenheck/TCF/rosenberg/
  // ebm/ZA/comefri/nyblower/basx/lau via the SHARED useRatingDispatch; chooseFan
  // fires the per-fan curve POST; ISP/ESP fires POST /ahu/fans/<id>/isp. SELECT
  // writes current_model.set via the form shim + persists curves (PUT
  // ahu/fans/curves/<id>) + stamps shim #9 fan_rating_curves_model. The
  // fan-only FAN CURVE CANVAS (a 2D <canvas>) renders the stored/rated curve.
  // Opened from the FAN FORM's rating label (withRatingBlock,
  // ratingBlockDataName='fan'); §30.4 key fan_<id>:selector:<action>.
  fan: {
    def: { name: 'fan', label: 'Fan' },
    render: (props) => <FanSelector {...props} />,
  },
};

export const getSelectorEntry = (name: string): SelectorRegistryEntry | undefined =>
  SELECTOR_REGISTRY[name];
